import { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import type { UserPreferences } from '../types';

interface UseTutorialParams {
  userPreferences: UserPreferences | undefined;
  setUserPreferences: React.Dispatch<React.SetStateAction<UserPreferences | undefined>>;
}

interface UseTutorialResult {
  showTutorialModal: boolean;
  showWalkthrough: boolean;
  setShowTutorialModal: React.Dispatch<React.SetStateAction<boolean>>;
  handleTutorialComplete: (dontShowAgain: boolean) => Promise<void>;
  handleReplayWalkthrough: () => void;
  handleWalkthroughComplete: () => void;
}

export default function useTutorial({
  userPreferences,
  setUserPreferences,
}: UseTutorialParams): UseTutorialResult {
  const [showTutorialModal, setShowTutorialModal] = useState(false);
  const [showWalkthrough, setShowWalkthrough] = useState(false);

  // Show tutorial modal on first login (once per session)
  useEffect(() => {
    if (userPreferences && !userPreferences.tutorial_completed) {
      const alreadyShown = sessionStorage.getItem('skillsnap_tutorial_shown');
      if (!alreadyShown) {
        setShowTutorialModal(true);
        sessionStorage.setItem('skillsnap_tutorial_shown', '1');
      }
    }
  }, [userPreferences]);

  const handleTutorialComplete = useCallback(async (dontShowAgain: boolean) => {
    setShowTutorialModal(false);
    if (dontShowAgain) {
      try {
        await api.updatePreferences({ tutorial_completed: true });
        setUserPreferences((prev) => prev ? { ...prev, tutorial_completed: true } : prev);
      } catch (err) {
        console.error('Failed to save tutorial preference:', err);
      }
    }
  }, [setUserPreferences]);

  const handleReplayWalkthrough = useCallback(() => {
    setShowWalkthrough(true);
  }, []);

  const handleWalkthroughComplete = useCallback(() => {
    setShowWalkthrough(false);
  }, []);

  return {
    showTutorialModal,
    showWalkthrough,
    setShowTutorialModal,
    handleTutorialComplete,
    handleReplayWalkthrough,
    handleWalkthroughComplete,
  };
}
